/**
 * Global session storage helper for preserving page state (filters, search terms, etc.)
 * Each page can have its own namespace to avoid conflicts
 */

const STORAGE_PREFIX = "showup_partner_";

/**
 * Get a namespaced key for a page
 * @param {string} pageKey - Page identifier (e.g., 'events_list', 'communities_list')
 * @param {string} key - The specific key within that page
 * @returns {string} - Full storage key
 */
const getStorageKey = (pageKey, key) => {
  return `${STORAGE_PREFIX}${pageKey}_${key}`;
};

/**
 * Save a value to session storage for a specific page
 * @param {string} pageKey - Page identifier
 * @param {string} key - The key to store
 * @param {any} value - The value to store (will be JSON stringified)
 */
export const saveToSessionStorage = (pageKey, key, value) => {
  try {
    const storageKey = getStorageKey(pageKey, key);
    const serializedValue = JSON.stringify(value);
    sessionStorage.setItem(storageKey, serializedValue);
  } catch (error) {
    console.warn(`Failed to save to session storage: ${error.message}`);
  }
};

/**
 * Get a value from session storage for a specific page
 * @param {string} pageKey - Page identifier
 * @param {string} key - The key to retrieve
 * @param {any} defaultValue - Default value if key doesn't exist
 * @returns {any} - The stored value or defaultValue
 */
export const getFromSessionStorage = (pageKey, key, defaultValue = null) => {
  try {
    const storageKey = getStorageKey(pageKey, key);
    const item = sessionStorage.getItem(storageKey);
    if (item === null || item === "undefined") {
      return defaultValue;
    }
    return JSON.parse(item);
  } catch (error) {
    console.warn(`Failed to read from session storage: ${error.message}`);
    return defaultValue;
  }
};

/**
 * Remove a value from session storage for a specific page
 * @param {string} pageKey - Page identifier
 * @param {string} key - The key to remove
 */
export const removeFromSessionStorage = (pageKey, key) => {
  try {
    const storageKey = getStorageKey(pageKey, key);
    sessionStorage.removeItem(storageKey);
  } catch (error) {
    console.warn(`Failed to remove from session storage: ${error.message}`);
  }
};

/**
 * Clear all session storage for a specific page
 * @param {string} pageKey - Page identifier
 */
export const clearPageSessionStorage = (pageKey) => {
  try {
    const prefix = getStorageKey(pageKey, "");
    Object.keys(sessionStorage).forEach((key) => {
      if (key.startsWith(prefix)) {
        sessionStorage.removeItem(key);
      }
    });
  } catch (error) {
    console.warn(`Failed to clear page session storage: ${error.message}`);
  }
};

/**
 * Save multiple values at once for a page
 * @param {string} pageKey - Page identifier
 * @param {object} data - Object with key-value pairs to save
 */
export const saveMultipleToSessionStorage = (pageKey, data) => {
  Object.entries(data).forEach(([key, value]) => {
    saveToSessionStorage(pageKey, key, value);
  });
};

/**
 * Get multiple values at once for a page
 * @param {string} pageKey - Page identifier
 * @param {object} defaults - Object with default values (keys are the storage keys)
 * @returns {object} - Object with retrieved values
 */
export const getMultipleFromSessionStorage = (pageKey, defaults = {}) => {
  const result = {};
  Object.keys(defaults).forEach((key) => {
    result[key] = getFromSessionStorage(pageKey, key, defaults[key]);
  });
  return result;
};
